import React from "react";
import { cva } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import { cn } from "../../lib/cn";

const buttonStyles = cva(
  "inline-flex items-center justify-center gap-2 rounded font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-neutral-900 disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none",
  {
    variants: {
      variant: {
        primary: "bg-primary-900 text-white hover:bg-primary-800",
        accent: "bg-accent-500 text-white hover:bg-accent-600",
        secondary:
          "bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-700 text-neutral-900 dark:text-neutral-100 hover:bg-neutral-50 dark:hover:bg-neutral-800",
        ghost:
          "bg-transparent text-neutral-700 dark:text-neutral-200 hover:bg-neutral-100 dark:hover:bg-neutral-800",
        danger: "bg-danger-500 text-white hover:bg-danger-700",
        "outline-primary":
          "bg-white dark:bg-neutral-900 border border-primary-900 dark:border-primary-400 text-primary-900 dark:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/30",
      },
      size: {
        xs: "h-7 px-2 text-xs",
        sm: "h-8 px-3 text-sm",
        md: "h-10 px-4 text-sm",
        lg: "h-12 px-6 text-base",
      },
      fullWidth: {
        true: "w-full",
        false: "",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
      fullWidth: false,
    },
  }
);

/**
 * Button
 * @param {"primary"|"accent"|"secondary"|"ghost"|"danger"|"outline-primary"} [variant="primary"]
 * @param {"xs"|"sm"|"md"|"lg"} [size="md"]
 * @param {boolean} [isLoading] - shows a spinner and disables the button
 * @param {React.ReactNode} [leftIcon]
 * @param {React.ReactNode} [rightIcon]
 * @param {boolean} [disabled]
 * @param {boolean} [fullWidth]
 * @param {string} [className]
 * @param {React.ReactNode} children
 */
export const Button = React.forwardRef(function Button(
  {
    variant = "primary",
    size = "md",
    isLoading = false,
    leftIcon,
    rightIcon,
    disabled = false,
    fullWidth = false,
    className,
    children,
    ...props
  },
  ref
) {
  const isDisabled = disabled || isLoading;

  return (
    <button
      ref={ref}
      type={props.type || "button"}
      disabled={isDisabled}
      aria-disabled={isDisabled}
      className={cn(buttonStyles({ variant, size, fullWidth }), className)}
      {...props}
    >
      {isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
      ) : (
        leftIcon
      )}
      {children}
      {!isLoading && rightIcon}
    </button>
  );
});
